import { Component, HostBinding, ViewChild } from '@angular/core';
import * as _ from 'lodash';
import { Endpoint, HelpIndex } from '../../../../models/enums';
import { CellTemplateType, GenericColDef, AlignDirection, CellActionType } from '../../../../models/grid-context';
import { Template } from '../../../../models/template';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { isNullOrUndefined } from '../../../../models/utils';
import { RowNode, CellClickedEvent, CellClassParams } from 'ag-grid-community';
import { SaveToOrgsModalComponent } from '../../save-to-orgs-modal/save-to-orgs-modal.component';
import { ObjectActionListType, ActionType, Permissions } from '../../../../models/permission';

@Component({
    standalone: false,
    selector: 'res-template-profiles',
    templateUrl: './template-profiles.component.html',
    styleUrls: ['./template-profiles.component.scss']
})

export class TemplateProfilesComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;
    isDirty = false;

    gridName = 'Template Profiles Grid';
    automationId = 'template-profiles';
    isBusy = false;
    isBusyMessage = '';
    currentHandle = '';
    currentTemplateProfiles: any[];
    originalTemplateProfiles: any[];

    validChanges = true;
    templateDeletedPresent = false;
    hasCreatePermissions = false;

    columnDefs: any;
    requestInfo: DrRequestInfo;

    rowClassRules = {
        'dark-row': (params: any): any => {
            return (params.data?.isParent);
        },
        'disabled-row': (params) => {
            return (!isNullOrUndefined(params.data.templateExists) && !params.data.templateExists);
        }
    };

    saveChanges(): void {
        this.isBusyMessage = 'Saving Template Profiles ...';
        this.isBusy = true;
        const savedTP = [];
        this.currentTemplateProfiles.forEach(tp => {
            if (tp.ownerHandle) {
                delete tp['isDisabled'];
                delete tp['isTemplateProfileRow'];
                delete tp['iconClass'];
                delete tp['iconColor'];
                delete tp['iconSecondClass'];
                savedTP.push(tp);
            }
        })
        this.restService.post(Endpoint.templateManager_updateMetaTemplateProfileEntries, {
            metaTemplateProfileEntries: savedTP,
            organizations: null,
            ownerHandle: this.currentHandle
        }).then(response => {
            this.isBusy = false;
            this.isDirty = false;
            if (response?.status === 'SUCCESS') {
                this.loadData();
            }
        });
    }

    discardChanges(): void {
        this.isDirty = false;
        this.loadData();
    }

    isDisabled(): boolean {
        let isDisabled = true;
        if (!this.isBusy && this.isDirty) {
            isDisabled = false;
        }
        return isDisabled;
    }

    isCheckboxDisabled(): boolean {
        return !this.hasCreatePermissions;
    }

    constructor(private commonService: CommonService, private restService: RestService) { }

    cellClickedCallback = (rowNode: RowNode, action: CellActionType, event?: CellClickedEvent) => {
        if (rowNode && rowNode.data && action && event) {
            if (action === CellActionType.checkbox) {
                const updatedRows = [];
                if (event.colDef.colId === 'SAVE_TO_ORG') {
                    rowNode.data.ownerHandle = isNullOrUndefined(rowNode.data.ownerHandle) ? this.currentHandle : null;
                    rowNode.data.defaultTemplate = false;
                } else {
                    if (!rowNode.data.defaultTemplate) {
                        event.api.forEachNode((node) => {
                            if (node.data.templateType === rowNode.data.templateType && node.data.defaultTemplate) {
                                node.data.defaultTemplate = false;
                                updatedRows.push(node);
                                return;
                            }
                        });
                    }
                    rowNode.data.defaultTemplate = !rowNode.data.defaultTemplate;
                }
                updatedRows.push(rowNode);
                event.api.redrawRows({ rowNodes: updatedRows });
                this.updateCurrentTemplateProfiles(updatedRows);
            }
        }
    }

    updateCurrentTemplateProfiles(updatedRows: any[]): void {
        updatedRows.forEach(row => {
            const data = row.data;
            const index = this.currentTemplateProfiles.findIndex(el => el.templateName === data.templateName && el.templateType === data.templateType && el.templateExists === data.templateExists);
            if (index > -1) {
                this.currentTemplateProfiles[index] = data;
            }
        });
        this.isDirty = false;
        for (let i = 0; i < this.originalTemplateProfiles.length; i++) {
            if (this.originalTemplateProfiles[i].ownerHandle !== this.currentTemplateProfiles[i].ownerHandle || this.originalTemplateProfiles[i].defaultTemplate !== this.currentTemplateProfiles[i].defaultTemplate) {
                this.isDirty = true;
                break;
            }
        }
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string): void {
        this.currentHandle = node.data instanceof Template ? node.data.handle : projectHandle;
        this.hasCreatePermissions = Permissions.checkUserPermission(ObjectActionListType.SYSTEM_META_TEMPLATE, ActionType.CREATE);
        this.columnDefs = this.getTemplateProfilesColumnDefs();
        this.isDirty = false;
        this.loadData();
    }

    loadData(): void {
        this.isBusy = true;
        this.isBusyMessage = 'Loading Template Profiles ...';
        this.isDirty = false;
        this.templateDeletedPresent = false;
        this.restService.post(Endpoint.templateManager_getMetaTemplateProfileEntries, { handle: this.currentHandle, systemScope: true }).then(response => {
            this.isBusy = false;
            if (response?.metaTemplateProfileEntries) {
                const nestedArray = [];
                this.currentTemplateProfiles = _.cloneDeep(response.metaTemplateProfileEntries);
                this.originalTemplateProfiles = _.cloneDeep(response.metaTemplateProfileEntries);
                this.templateDeletedPresent = (response.metaTemplateProfileEntries.filter(tp => !tp.templateExists)).length > 0;
                response.metaTemplateProfileEntries.forEach(templateProfile => {
                    templateProfile.isDisabled = !templateProfile.templateExists;
                    const found = nestedArray.find(element => element.templateType === templateProfile.templateType);
                    if (found) {
                        templateProfile.displayValue = '';
                        found.children.push(templateProfile);
                    } else {
                        const childProfile = Object.assign({}, templateProfile)
                        childProfile.displayValue = '';
                        nestedArray.push({
                            displayValue: templateProfile.displayValue, templateType: templateProfile.templateType, children: [childProfile], isExpanded: true, isParent: true,
                            iconClass: 'fa-sticky-note-o', iconColor: 'color-purple-03', iconSecondClass: 'fa-sticky-note', isTemplateProfileRow: true
                        });
                    }
                });
                this.genericGrid.setData(nestedArray, true);
                this.genericGrid.gridApi.sizeColumnsToFit();
            }
        }, error => {
            this.isBusy = false;
        });
    }

    saveToOrganizations(): void {
        this.commonService.openModal(SaveToOrgsModalComponent, {
            modalData: {
                ownerHandle: this.currentHandle
            },
            class: 'vertical-px-600 horizontal-px-500'
        });
    }

    openHelp = () => this.commonService.openHelp(HelpIndex.sys_template_profiles_dlg);

    getTemplateProfilesColumnDefs(): Array<any> {
        return new Array<GenericColDef>(
            {
                headerName: 'Template Type',
                field: 'displayValue',
                width: 250,
                automationId: this.automationId,
                cellTemplateType: CellTemplateType.nameMultipleIconsLabel,
                suppressSizeToFit: true,
                lockVisible: true
            },
            {
                headerName: 'Save to Org',
                colId: 'SAVE_TO_ORG',
                valueGetter: (params: any) => params.data.isParent ? null : !isNullOrUndefined(params.data.ownerHandle),
                automationId: this.automationId,
                cellTemplateType: this.isCheckboxDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                headerAlignDirection: AlignDirection.center,
                cellAlignDirection: AlignDirection.center,
                width: 100,
                suppressSizeToFit: true,
                lockVisible: true
            },
            {
                headerName: 'Set as Default',
                colId: 'SET_AS_DEFAULT',
                field: 'defaultTemplate',
                automationId: this.automationId,
                cellClass: (cellClassParams: CellClassParams) => !cellClassParams.data.isParent && isNullOrUndefined(cellClassParams.data.ownerHandle) ? 'ag-cell-disabled' : 'green-checkbox',
                cellTemplateType: this.isCheckboxDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                headerAlignDirection: AlignDirection.center,
                cellAlignDirection: AlignDirection.center,
                width: 130,
                suppressSizeToFit: true,
                lockVisible: true
            },
            {
                headerName: 'Template Name',
                field: 'templateName',
                automationId: this.automationId,
                lockVisible: true
            },
            {
                headerName: 'Template Description',
                field: 'templateDescription',
                automationId: this.automationId,
                lockVisible: true
            })
    }
}